import React, { useState, useEffect, useCallback } from 'react';
import UnifiedDataExplorer from './UnifiedDataExplorer';
import USMap from './USMap';
import DataLegend from './DataLegend';
import StateDetails from './StateDetails';
import StateList from './StateList';
import StateGovernment from './StateGovernment';
import AboutSection from './AboutSection';
import CensusDataExplorer from './CensusDataExplorer';
import StateDataVisualizations from './StateDataVisualizations';
import AmericanFlag from './AmericanFlag';
import { StarsAndStripesHeader } from './PatrioticDecor';
import SearchAndFilter from './SearchAndFilter';
import StateComparison from './StateComparison';
import { APP_CONFIG, THEME } from '../constants/appConfig';
import dataService from '../services/dataService';

const DesktopLayout = ({ 
    selectedMetric,
    onMetricChange,
    stateData,
    selectedState,
    onStateClick,
    loading,
    error 
}) => {
    const [compareStates, setCompareStates] = useState([]);
    const [showWhatsNew, setShowWhatsNew] = useState(true);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [rightPanelTab, setRightPanelTab] = useState('overview'); // 'overview', 'metrics', 'compare', 'trends', 'voter', 'about'

    // Handle window resize for responsive behavior
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 1024) {
                setSidebarCollapsed(true);
            } else {
                setSidebarCollapsed(false);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Call once on mount

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (e) => {
            // Forward slash focuses search
            if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                document.querySelector('#search-input')?.focus();
            }
            // Escape clears selection
            if (e.key === 'Escape') {
                onStateClick(null);
            }
            // Question mark shows help
            if (e.key === '?' && e.shiftKey) {
                alert('Keyboard Shortcuts:\n/ - Focus search\nEsc - Clear selection\n? - Show this help');
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [onStateClick]);


    // Compare handler
    const handleCompare = useCallback((states) => {
        setCompareStates(states);
    }, []);

    const getCurrentMetricDescription = () => {
        // This would normally come from the data service
        const descriptions = {
            'medianIncome': 'Median Household Income',
            'unemployment': 'Unemployment Rate',
            'population': 'Total Population',
        };
        return descriptions[selectedMetric] || selectedMetric || 'No metric selected';
    };


    const renderHeader = () => (
        <div style={{
            gridArea: 'header',
            backgroundColor: THEME.colors.dark,
            color: 'white',
            padding: '12px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 1000
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <AmericanFlag width={48} height={28} />
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'white' }}>
                        {APP_CONFIG.shortName}
                    </div>
                </div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>
                    {APP_CONFIG.description}
                </div>
                <div style={{
                    fontSize: '11px',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontWeight: 'bold'
                }}>
                    v{APP_CONFIG.version}
                </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                {/* Quick Search */}
                <input
                    type="text"
                    placeholder="Quick search..."
                    style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: 'none',
                        fontSize: '14px',
                        width: '200px'
                    }}
                />
                
                {/* Data Source Status */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ 
                        width: '8px', 
                        height: '8px', 
                        backgroundColor: '#4CAF50', 
                        borderRadius: '50%' 
                    }}></div>
                    <span style={{ fontSize: '12px' }}>Live Data</span>
                </div>
                
                {/* GitHub Star */}
                <a 
                    href="https://github.com/8bits1beard-io/project-theamerica" 
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        backgroundColor: 'transparent',
                        border: '1px solid rgba(255,255,255,0.3)',
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}
                >
                    ⭐ Star on GitHub
                </a>
                
                {/* Settings */}
                <button 
                    onClick={() => setRightPanelTab('settings')}
                    style={{
                        backgroundColor: 'transparent',
                        border: '1px solid rgba(255,255,255,0.3)',
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer'
                    }}
                >
                    ⚙️ Settings
                </button>
            </div>
        </div>
    );

    const renderNavigation = () => (
        <div style={{
            gridArea: 'nav',
            background: `linear-gradient(90deg, ${THEME.colors.patrioticRed} 0%, ${THEME.colors.patrioticBlue} 100%)`,
            color: 'white',
            padding: '8px 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingLeft: '20px',
            paddingRight: '20px',
            borderBottom: `2px solid ${THEME.colors.patrioticWhite}`
        }}>
            <div style={{ display: 'flex', gap: '30px', alignItems: 'center' }}>
                

                <div style={{ 
                    height: '20px', 
                    width: '1px', 
                    backgroundColor: 'rgba(255,255,255,0.3)' 
                }}></div>

                <button style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '13px',
                    cursor: 'pointer'
                }}>
                    ⭐ Favorites
                </button>

                <button style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '13px',
                    cursor: 'pointer'
                }}>
                    🕒 Recent
                </button>
            </div>

            <div style={{ display: 'flex', gap: '15px' }}>
                <button style={{
                    backgroundColor: 'transparent',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer'
                }}>
                    📤 Export
                </button>
            </div>
        </div>
    );

    const renderSidebar = () => (
        <div style={{
            gridArea: 'sidebar',
            backgroundColor: THEME.colors.light,
            borderRight: `2px solid ${THEME.colors.patrioticBlue}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            width: sidebarCollapsed ? '60px' : '320px',
            transition: 'width 0.3s ease',
            background: `linear-gradient(180deg, ${THEME.colors.backgroundAccent} 0%, ${THEME.colors.light} 100%)`
        }}>
            {/* Collapse Toggle */}
            <div style={{
                padding: '10px',
                borderBottom: '1px solid #e9ecef',
                display: 'flex',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-end'
            }}>
                <button
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    style={{
                        backgroundColor: 'transparent',
                        border: '1px solid #ddd',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                    }}
                >
                    {sidebarCollapsed ? '▶' : '◀'}
                </button>
            </div>

            {!sidebarCollapsed ? (
                <div style={{ flex: 1, overflow: 'hidden' }}>
                    <UnifiedDataExplorer
                        onMetricSelect={onMetricChange}
                        selectedMetric={selectedMetric}
                    />
                </div>
            ) : (
                <div style={{ padding: '10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', marginBottom: '10px' }}>📊</div>
                    <div style={{ writingMode: 'vertical-rl', fontSize: '12px' }}>Categories</div>
                </div>
            )}
        </div>
    );

    const renderMapArea = () => (
        <div style={{
            gridArea: 'map',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto'
        }}>
            {/* Search and Filter */}
            <SearchAndFilter
                onSearch={() => {}}
                onFilter={() => {}}
                availableMetrics={Object.keys(stateData || {})}
                availableStates={dataService.getAllStates()}
                onCompare={handleCompare}
            />
            
            {/* Map Controls */}
            <div style={{
                padding: '16px',
                backgroundColor: '#fff',
                borderBottom: '1px solid #e9ecef',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px'
            }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: THEME.colors.patrioticBlue }}>
                        Currently Showing:
                    </div>
                    <div style={{ fontSize: '16px', color: THEME.colors.patrioticRed, fontWeight: 'bold' }}>
                        {getCurrentMetricDescription()}
                    </div>
                </div>
                
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button style={{
                        padding: '6px 12px',
                        backgroundColor: THEME.colors.light,
                        border: `2px solid ${THEME.colors.patrioticBlue}`,
                        borderRadius: '4px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        color: THEME.colors.patrioticBlue,
                        fontWeight: '500'
                    }}>
                        📅 Time Period
                    </button>
                    <button style={{
                        padding: '6px 12px',
                        backgroundColor: THEME.colors.light,
                        border: `2px solid ${THEME.colors.patrioticBlue}`,
                        borderRadius: '4px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        color: THEME.colors.patrioticBlue,
                        fontWeight: '500'
                    }}>
                        🎨 Map Style
                    </button>
                </div>
            </div>

            {/* Map */}
            <div style={{ flex: 1, position: 'relative', padding: '16px' }}>
                {loading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                    }}>
                        <div style={{ fontSize: '16px' }}>Loading data...</div>
                    </div>
                )}
                
                {error && (
                    <div style={{
                        padding: '20px',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffeaa7',
                        borderRadius: '4px',
                        color: '#856404',
                        textAlign: 'center',
                        margin: '20px'
                    }}>
                        <strong>Error:</strong> {error}
                    </div>
                )}
                
                {/* Show map view */}
                {false ? (
                    <div style={{ padding: '20px', overflow: 'auto', flex: 1 }}>
                        {compareStates.length === 0 && (
                            <div style={{ 
                                textAlign: 'center', 
                                padding: '40px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                marginBottom: '20px'
                            }}>
                                <h3>⚖️ Comparison Mode</h3>
                                <p>Select states to compare by:</p>
                                <ul style={{ textAlign: 'left', display: 'inline-block', margin: '20px 0' }}>
                                    <li>Using the search bar above</li>
                                    <li>Clicking states on the map</li>
                                    <li>Using the state list in the sidebar</li>
                                </ul>
                                <p>You can compare up to 5 states at once.</p>
                            </div>
                        )}
                        <StateComparison 
                            states={compareStates}
                            metrics={null}
                        />
                    </div>
                ) : (
                    <div style={{ position: 'relative', flex: 1 }}>
                        <USMap 
                            selectedMetric={selectedMetric}
                            stateData={stateData}
                            onStateClick={onStateClick}
                            selectedState={selectedState}
                        />
                    </div>
                )}
            </div>

            {/* Legend */}
            <div style={{ 
                borderTop: '1px solid #e9ecef',
                backgroundColor: '#fff',
                minHeight: '120px'
            }}>
                <DataLegend 
                    selectedMetric={selectedMetric}
                    stateData={stateData}
                    selectedState={selectedState}
                />
            </div>
            
            {/* State Data Visualizations - Show when state is selected */}
            {selectedState && (
                <div style={{ 
                    padding: '20px',
                    backgroundColor: '#fff',
                    borderTop: '2px solid #e9ecef'
                }}>
                    <StateDataVisualizations 
                        selectedState={selectedState}
                    />
                </div>
            )}
        </div>
    );

    const renderRightPanel = () => (
        <div style={{
            gridArea: 'details',
            backgroundColor: '#fff',
            borderLeft: '1px solid #e9ecef',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Tab Navigation */}
            <div style={{
                borderBottom: '1px solid #e9ecef',
                display: 'flex',
                backgroundColor: '#f8f9fa'
            }}>
                {[
                    { key: 'overview', label: 'Overview', icon: '📋' },
                    { key: 'metrics', label: 'All Metrics', icon: '📊' },
                    { key: 'census', label: 'Census Data', icon: '🏛️' },
                    { key: 'about', label: 'About', icon: 'ℹ️' }
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setRightPanelTab(tab.key)}
                        style={{
                            flex: 1,
                            padding: '12px 8px',
                            backgroundColor: rightPanelTab === tab.key ? '#fff' : 'transparent',
                            border: 'none',
                            borderBottom: rightPanelTab === tab.key ? '2px solid #4CAF50' : '2px solid transparent',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: rightPanelTab === tab.key ? '500' : 'normal',
                            color: rightPanelTab === tab.key ? '#4CAF50' : '#666',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <span style={{ fontSize: '14px' }}>{tab.icon}</span>
                        <span>{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div style={{ flex: 1, overflow: 'auto' }}>
                {rightPanelTab === 'overview' && (
                    <>
                        <StateDetails 
                            selectedState={selectedState}
                            allData={selectedMetric && stateData ? { [selectedMetric]: stateData } : {}}
                        />
                        <StateGovernment 
                            selectedState={selectedState}
                        />
                    </>
                )}
                
                {rightPanelTab === 'metrics' && (
                    <StateList 
                        selectedMetric={selectedMetric}
                        stateData={stateData}
                        onStateClick={onStateClick}
                        selectedState={selectedState}
                    />
                )}
                
                {rightPanelTab === 'census' && (
                    <CensusDataExplorer 
                        onDataSelect={(data) => {
                            console.log('Census data selected:', data);
                            // You can integrate this data with your existing state management
                        }}
                    />
                )}
                
                {rightPanelTab === 'about' && (
                    <AboutSection />
                )}

                {rightPanelTab === 'settings' && (
                    <div style={{ padding: '20px' }}>
                        <h3 style={{ marginBottom: '20px' }}>⚙️ Settings</h3>
                        
                        <div style={{ marginBottom: '20px' }}>
                            <h4>Display Options</h4>
                            <label style={{ display: 'block', margin: '10px 0' }}>
                                <input type="checkbox" defaultChecked /> Show state labels on map
                            </label>
                            <label style={{ display: 'block', margin: '10px 0' }}>
                                <input type="checkbox" defaultChecked /> Enable animations
                            </label>
                            <label style={{ display: 'block', margin: '10px 0' }}>
                                <input type="checkbox" /> Dark mode
                            </label>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <h4>Data Preferences</h4>
                            <label style={{ display: 'block', margin: '10px 0' }}>
                                <input type="checkbox" defaultChecked /> Auto-refresh data
                            </label>
                            <label style={{ display: 'block', margin: '10px 0' }}>
                                Default metric:
                                <select style={{ marginLeft: '10px' }}>
                                    <option>Population</option>
                                    <option>Income</option>
                                    <option>Education</option>
                                </select>
                            </label>
                        </div>

                        <button style={{
                            padding: '8px 16px',
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}>
                            Save Settings
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    const renderStatusBar = () => (
        <div style={{
            gridArea: 'status',
            background: `linear-gradient(90deg, ${THEME.colors.patrioticBlue} 0%, ${THEME.colors.dark} 100%)`,
            borderTop: `2px solid ${THEME.colors.patrioticRed}`,
            padding: '8px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '12px',
            color: 'white'
        }}>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                <span style={{ fontWeight: 'bold', color: THEME.colors.warning }}>
                    📊 100% Taxpayer Funded Data
                </span>
                <span>
                    <strong>State:</strong> {selectedState ? 
                        selectedState : 'Click map to select'}
                </span>
                <span>
                    <strong>Metric:</strong> {getCurrentMetricDescription()}
                </span>
            </div>
            
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', fontStyle: 'italic' }}>
                    All data collected & maintained with your tax dollars
                </span>
                <span style={{ color: '#4CAF50' }}>
                    ● Live
                </span>
            </div>
        </div>
    );

    // Grid template areas based on screen size and sidebar state
    const getGridTemplate = () => {
        const baseTemplate = {
            gridTemplateAreas: `
                "header header header"
                "nav nav nav"
                "sidebar map details"
                "status status status"`,
            gridTemplateColumns: sidebarCollapsed ? '60px 1fr 400px' : '320px 1fr 400px',
            gridTemplateRows: 'auto auto 1fr auto'
        };

        if (window.innerWidth < 1440) {
            return {
                ...baseTemplate,
                gridTemplateAreas: `
                    "header header"
                    "nav nav"
                    "sidebar map"
                    "status status"`,
                gridTemplateColumns: sidebarCollapsed ? '60px 1fr' : '280px 1fr'
            };
        }

        return baseTemplate;
    };

    return (
        <>
            {/* What's New Banner */}
            {showWhatsNew && (
                <div style={{
                    backgroundColor: THEME.colors.patrioticBlue,
                    color: 'white',
                    padding: '10px',
                    textAlign: 'center',
                    fontSize: '14px',
                    position: 'relative'
                }}>
                    <span>
                        🆕 <strong>v{APP_CONFIG.version} Released!</strong> - 
                        Data visualization best practices applied • 100% House Representatives data coverage • 
                        Colorblind-friendly design • National benchmarks on all charts
                    </span>
                    <button
                        onClick={() => setShowWhatsNew(false)}
                        style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            fontSize: '18px',
                            cursor: 'pointer'
                        }}
                    >
                        ×
                    </button>
                </div>
            )}
            
            <div style={{
                display: 'grid',
                ...getGridTemplate(),
                gap: '0',
                height: showWhatsNew ? 'calc(100vh - 40px)' : '100vh',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
                {renderHeader()}
                <StarsAndStripesHeader height={6} />
                {renderNavigation()}
                {renderSidebar()}
                {renderMapArea()}
                {window.innerWidth >= 1440 && renderRightPanel()}
                {renderStatusBar()}
            </div>
        </>
    );
};

export default DesktopLayout;